import { Storage } from '../lib/storage';
import { applySerieResult } from '../engine/seriespillResults';
import type { SeriesKey } from './useSeries';

// ─────────────────────────────────────────────────────────────────────
// useSeriesActions — the seriespill (league) action handlers lifted verbatim
// out of Padel24AppMain (extracted from the former monolith) as a god-component
// collapse slice (PR-AY). recordSerieResultForTournament reads effectivePlayerDB (from
// useDerivations) + applySerieResult, so the hook is called AFTER useDerivations
// in Padel24AppMain. persistSeries / persistPausedSeries are private to the hook
// (internal-only). All writes are Storage-first (await Storage.set before
// setState). These handlers touch only the active/paused series state + their
// setters — NOT the R1 §13.20 loaded-flag refs (those stay in useSeries; the
// blanket-persist effects remain the sole flag-gated writers), and they only
// run on user action (after load) so they never write-before-load.
//
// A WRITE THAT FAILS STOPS THE FLOW (#1311). Each writer reports the failure
// (`reportSeriesSaveFailure` — the toast with «Last inn på nytt») and answers
// `false`; the caller then does NOT move the state, and shows no success
// toast over the failure. Before, the failure was only logged and the state
// moved anyway, so «Storage-first» held in the code's order and not in effect.
// ─────────────────────────────────────────────────────────────────────
export interface UseSeriesActionsDeps {
  activeSeries: any;
  setActiveSeries: (v: any) => void;
  pausedSeries: any;
  setPausedSeries: (v: any) => void;
  showToast: (kind: any, msg: any) => void;
  setView: (v: any) => void;
  effectivePlayerDB: any;
  /** From useSeries — reports a series write that did not land (#1311). */
  reportSeriesSaveFailure: (key: SeriesKey, error: unknown) => void;
}

export function useSeriesActions({
  activeSeries,
  setActiveSeries,
  pausedSeries,
  setPausedSeries,
  showToast,
  setView,
  effectivePlayerDB,
  reportSeriesSaveFailure,
}: UseSeriesActionsDeps) {
  const recordSerieResultForTournament = (updatedMatch: any) => {
    if (!activeSeries) return;
    updateActiveSeries(applySerieResult(activeSeries, updatedMatch, effectivePlayerDB));
  };

  // Persistence for the active series (its own track — independent of tournament/paused).
  // Answers whether the write landed.
  const persistSeries = async (series: any): Promise<boolean> => {
    try {
      if (series == null) await Storage.set('active_series_v1', '');
      else await Storage.set('active_series_v1', JSON.stringify(series));
      return true;
    } catch (e) {
      reportSeriesSaveFailure('active_series_v1', e);
      return false;
    }
  };
  // Storage-first update of the series (HANDOVER rule: persist BEFORE setState).
  // A failed write leaves the state where it was and answers `false`, so a
  // caller can skip its success toast.
  const updateActiveSeries = async (next: any): Promise<boolean> => {
    if (!(await persistSeries(next))) return false;
    setActiveSeries(next);
    return true;
  };

  // Persist a serie match's IN-PROGRESS score (liveScore) continuously, WITHOUT
  // finalising it (no ELO, no result) — the live banepad score. Storage-first.
  const recordSerieLiveScore = (updatedMatch: any) => {
    if (!activeSeries) return undefined;
    return updateActiveSeries({
      ...activeSeries,
      matches: (activeSeries.matches || []).map((m: any) =>
        m.id === updatedMatch.id ? { ...m, liveScore: updatedMatch.liveScore } : m,
      ),
    });
  };

  // Persistence for paused series. Answers whether the write landed.
  const persistPausedSeries = async (list: any): Promise<boolean> => {
    try {
      await Storage.set('paused_series_v1', JSON.stringify(list));
      return true;
    } catch (e) {
      reportSeriesSaveFailure('paused_series_v1', e);
      return false;
    }
  };

  // Pause the active series: move it to the pausedSeries list, remove it from the active slot.
  // The series keeps all its data (pairs, matches, standings, history). Storage-first:
  // write the paused list and clear the active slot in one atomic flow.
  // The paused list is written FIRST: if clearing the active slot then fails,
  // the series is stored twice (recoverable), never in neither key.
  const handlePauseSeries = async (): Promise<boolean> => {
    if (!activeSeries) return false;
    const snapshot = {
      ...activeSeries,
      pausedAt: Date.now(),
      // If the series lacks an id, give it one so we can re-find it later
      id: activeSeries.id || `series_paused_${Date.now()}_${Math.floor(Math.random() * 1000000)}`,
    };
    const nextList = [snapshot, ...pausedSeries.filter((p) => p.id !== snapshot.id)];
    if (!(await persistPausedSeries(nextList))) return false;
    setPausedSeries(nextList);
    if (!(await updateActiveSeries(null))) return false;
    showToast('success', `Serie «${snapshot.config?.name || 'Seriespill'}» satt på pause`);
    return true;
  };

  // Resume a paused series: move it back into the active slot, remove it from the paused list.
  // Requires that there is NOT already an active series (the UI should handle that).
  // The active slot is written FIRST (#1311). The other order removed the series
  // from the paused list before it was stored as active, so a failure of the
  // second write left it in neither key — lost. This way a failure of the
  // second write leaves it in both, and nothing is lost.
  const handleResumeSeries = async (seriesId: any) => {
    if (activeSeries) {
      showToast('error', 'Du har allerede en aktiv serie. Pause den først.');
      return;
    }
    const target = pausedSeries.find((p) => p.id === seriesId);
    if (!target) return;
    const { pausedAt: _pausedAt, ...rest } = target;
    const nextList = pausedSeries.filter((p) => p.id !== seriesId);
    if (!(await updateActiveSeries(rest))) return;
    if (!(await persistPausedSeries(nextList))) return;
    setPausedSeries(nextList);
    setView('seriespill');
    showToast('success', `Serie «${rest.config?.name || 'Seriespill'}» gjenopptatt`);
  };

  // Permanently delete a paused series (without archiving matches — because the pause flow
  // has not committed ELO to the player DB). Used for cleanup.
  const handleDeletePausedSeries = async (seriesId: any) => {
    const nextList = pausedSeries.filter((p) => p.id !== seriesId);
    if (!(await persistPausedSeries(nextList))) return;
    setPausedSeries(nextList);
    showToast('success', 'Pauset serie slettet');
  };

  return {
    recordSerieResultForTournament,
    recordSerieLiveScore,
    updateActiveSeries,
    handlePauseSeries,
    handleResumeSeries,
    handleDeletePausedSeries,
  };
}
